import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert, FileText, Globe, Plus, ShieldCheck, Trash2, Upload } from "lucide-react";
import { CREDIT_COSTS, minorToDecimalString, parseMoneyToMinor, type ImportedMenuItem, type ImportExtraction, type ImportJobDto } from "@dishfolio/contracts";
import { getTheme } from "@dishfolio/themes";
import { Badge, Button, Card, CardDescription, CardHeader, CardTitle, Checkbox, EmptyState, Field, Input, NativeSelect, Progress, Switch, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, cn, toast } from "@dishfolio/ui";
import { api } from "../lib/api.ts";
import { useOrg } from "../lib/org.tsx";
import { useImport, useMenus, useRestaurants } from "../lib/queries.ts";
import { PageContainer, PageHeader } from "../components/Page.tsx";
import { notifyError, UpgradeCard } from "../components/Upgrade.tsx";

export function NewImportPage() {
  const search = useSearch({ from: "/app/imports/new" });
  const navigate = useNavigate();
  const { org } = useOrg();
  const restaurants = useRestaurants();
  const menus = useMenus();
  const [type, setType] = useState<"website" | "pdf">(search.type);
  const [restaurantId, setRestaurantId] = useState(search.restaurantId ?? "");
  const [menuId, setMenuId] = useState(search.menuId ?? "");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [rights, setRights] = useState(false);
  const [loading, setLoading] = useState(false);
  const rid = restaurantId || restaurants.data?.[0]?.id || "";
  const credits = org?.subscription.usage.creditsRemaining ?? 0;
  const cost = type === "website" ? CREDIT_COSTS.websiteImport : CREDIT_COSTS.pdfImport;
  if (org && !org.plan.imports) {
    return (
      <PageContainer>
        <PageHeader title="Import a menu" />
        <UpgradeCard title="Imports are a Pro feature" description="Paste your website or upload a PDF and Dishfolio builds an editable, multilingual menu with your colors and fonts — then you review everything before publishing." />
      </PageContainer>
    );
  }
  const start = async () => {
    setLoading(true);
    try {
      let job: ImportJobDto;
      if (type === "website") {
        job = (await api.post<{ job: ImportJobDto }>("/imports/website", { restaurantId: rid, menuId: menuId || undefined, url: url.match(/^https?:\/\//) ? url : `https://${url}`, confirmRights: rights })).job;
      } else {
        const form = new FormData();
        form.append("purpose", "import_pdf");
        form.append("file", file!);
        const up = await api.post<{ asset: { id: string } }>("/media", form);
        job = (await api.post<{ job: ImportJobDto }>("/imports/pdf", { restaurantId: rid, menuId: menuId || undefined, assetId: up.asset.id, confirmRights: rights })).job;
      }
      await navigate({ to: "/imports/$jobId", params: { jobId: job.id } });
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <PageContainer>
      <PageHeader title="Import a menu" description="We extract categories, dishes and prices plus your colors and fonts. Nothing is published until you review it." />
      <Card className="gap-5">
        <Tabs value={type} onValueChange={(v) => setType(v as "website" | "pdf")}>
          <TabsList>
            <TabsTrigger value="website">
              <Globe /> Website
            </TabsTrigger>
            <TabsTrigger value="pdf">
              <FileText /> PDF
            </TabsTrigger>
          </TabsList>
          <TabsContent value="website" className="grid gap-4 pt-2">
            <Field label="Website address" hint={`We visit up to ${org?.plan.maxImportWebsitePages ?? 10} pages of a public site. Sign-in pages and paywalls are never bypassed.`}>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your-restaurant.com" inputMode="url" />
            </Field>
          </TabsContent>
          <TabsContent value="pdf" className="grid gap-4 pt-2">
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed p-8 text-center hover:bg-accent">
              <Upload className="size-6 text-muted-foreground" />
              <span className="font-medium">{file ? file.name : "Choose a PDF menu"}</span>
              <span className="text-xs text-muted-foreground">
                Text or scanned PDFs, up to {Math.round((org?.plan.maxImportPdfBytes ?? 0) / 1024 / 1024)} MB and {org?.plan.maxImportPdfPages} pages. Scanned pages use {CREDIT_COSTS.analyzePdfPage} extra credits each.
              </span>
              <input type="file" accept="application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
          </TabsContent>
        </Tabs>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Location">
            <NativeSelect value={rid} onChange={(e) => setRestaurantId(e.target.value)}>
              {restaurants.data?.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Import into">
            <NativeSelect value={menuId} onChange={(e) => setMenuId(e.target.value)}>
              <option value="">A new menu</option>
              {menus.data
                ?.filter((m) => m.restaurantId === rid)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    Existing: {m.name}
                  </option>
                ))}
            </NativeSelect>
          </Field>
        </div>
        <label className="flex items-start gap-3 rounded-lg border bg-muted/40 p-3 text-sm">
          <Checkbox checked={rights} onCheckedChange={(v) => setRights(Boolean(v))} className="mt-0.5" />
          <span>
            <span className="flex items-center gap-1.5 font-medium">
              <ShieldCheck className="size-4" /> I own this content or have permission to use it
            </span>
            <span className="text-muted-foreground">Texts, logos and photos copied from the source may only be published with the rights holder’s permission.</span>
          </span>
        </label>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">
            Uses {cost} AI credits · {credits} left this month. Failed imports are refunded.
          </span>
          <Button onClick={start} loading={loading} disabled={!rights || !rid || (type === "website" ? !url.trim() : !file) || credits < cost}>
            Start import
          </Button>
        </div>
      </Card>
    </PageContainer>
  );
}

function ItemEditor({ item, currency, onChange, onRemove, selected, onSelect }: { item: ImportedMenuItem; currency: string; onChange: (i: ImportedMenuItem) => void; onRemove: () => void; selected: boolean; onSelect: () => void }) {
  const [price, setPrice] = useState(item.priceMinor != null ? minorToDecimalString(item.priceMinor, currency) : "");
  useEffect(() => setPrice(item.priceMinor != null ? minorToDecimalString(item.priceMinor, currency) : ""), [item.priceMinor, currency]);
  return (
    <div onClick={onSelect} className={cn("grid gap-2 rounded-lg border bg-card p-3", item.needsReview && "border-warning/60 bg-warning/5", selected && "ring-2 ring-primary/30")}>
      <div className="flex gap-2">
        <Input value={item.name} onChange={(e) => onChange({ ...item, name: e.target.value })} className="h-8 font-medium" />
        <Input
          value={price}
          inputMode="decimal"
          placeholder="Price"
          onChange={(e) => {
            setPrice(e.target.value);
            const minor = e.target.value.trim() ? parseMoneyToMinor(e.target.value, currency) : null;
            onChange({ ...item, priceMinor: minor ?? undefined });
          }}
          className="h-8 w-24"
        />
        <Button variant="ghost" size="icon-sm" onClick={onRemove} aria-label="Remove item">
          <Trash2 />
        </Button>
      </div>
      <Textarea value={item.description ?? ""} onChange={(e) => onChange({ ...item, description: e.target.value || undefined })} rows={1} placeholder="Description" className="min-h-9 text-sm" />
      {item.variants?.length ? <div className="text-xs text-muted-foreground">Sizes: {item.variants.map((v) => `${v.name} ${minorToDecimalString(v.priceMinor, currency)}`).join(" · ")}</div> : null}
      {item.needsReview ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {item.reviewReasons.map((r) => (
            <Badge key={r} variant="warning">
              <CircleAlert /> {r}
            </Badge>
          ))}
        </div>
      ) : null}
      {item.allergenText ? <div className="text-xs text-muted-foreground">Allergen note in source: “{item.allergenText}” — you’ll confirm allergens per dish before publishing.</div> : null}
    </div>
  );
}

function Review({ job }: { job: ImportJobDto }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { org } = useOrg();
  const restaurants = useRestaurants();
  const [ex, setEx] = useState<ImportExtraction>(job.extraction!);
  const [selected, setSelected] = useState<number | null>(null);
  const [mode, setMode] = useState<"new_menu" | "replace" | "append">(job.menuId ? "append" : "new_menu");
  const [menuName, setMenuName] = useState(job.extraction?.restaurantName ? `${job.extraction.restaurantName}` : "Imported menu");
  const [applyDesign, setApplyDesign] = useState(true);
  const [applying, setApplying] = useState(false);
  const currency = ex.detectedCurrency ?? restaurants.data?.find((r) => r.id === job.restaurantId)?.currency ?? "EUR";
  const categories = [...new Set(ex.items.map((i) => i.category))];
  const reviewCount = ex.items.filter((i) => i.needsReview).length;
  const sel = selected != null ? ex.items[selected] : null;
  const pages = (ex.pages ?? []) as Array<{ index: number; url?: string; screenshotUrl?: string; title?: string }>;
  const page = sel?.sourcePage ? pages.find((p) => p.index === sel.sourcePage) : pages[0];
  const theme = getTheme(ex.design?.suggestedThemeId ?? "basic");
  const themeLocked = theme && org && org.plan.themes !== "all" && !org.plan.themes.includes(theme.key);
  const update = (i: number, item: ImportedMenuItem) => setEx({ ...ex, items: ex.items.map((x, j) => (j === i ? item : x)) });
  const apply = async () => {
    setApplying(true);
    try {
      const res = await api.post<{ menuId: string }>(`/imports/${job.id}/apply`, { extraction: ex, mode, menuName, applyDesign });
      await qc.invalidateQueries({ queryKey: ["menus"] });
      toast.success("Imported into a draft — review flagged items, then publish.");
      await navigate({ to: "/menus/$menuId", params: { menuId: res.menuId } });
    } catch (err) {
      notifyError(err);
    } finally {
      setApplying(false);
    }
  };
  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card p-4">
        <div className="flex-1">
          <div className="font-medium">
            Found {ex.items.length} dishes in {categories.length} categories
          </div>
          <div className="text-sm text-muted-foreground">{reviewCount ? `${reviewCount} need a closer look (highlighted).` : "Everything looked clear — still give it a quick check."}</div>
        </div>
        {ex.warnings.length ? (
          <ul className="w-full list-disc pl-5 text-xs text-muted-foreground">
            {ex.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="content-start lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
          <CardHeader>
            <CardTitle>Source</CardTitle>
            <CardDescription>{sel ? "Evidence for the selected dish" : "Select a dish to see where it came from"}</CardDescription>
          </CardHeader>
          {sel?.sourceText ? <div className="rounded-md bg-muted p-3 font-mono text-xs whitespace-pre-wrap">{sel.sourceText}</div> : null}
          {page?.screenshotUrl ? (
            <div className="relative overflow-hidden rounded-lg border">
              <img src={page.screenshotUrl} alt={page.title ?? "Source page"} className="w-full" />
              {sel?.sourceRegion ? (
                <div
                  className="absolute border-2 border-primary bg-primary/10"
                  style={{ left: `${sel.sourceRegion.x * 100}%`, top: `${sel.sourceRegion.y * 100}%`, width: `${sel.sourceRegion.width * 100}%`, height: `${Math.max(sel.sourceRegion.height * 100, 1.5)}%` }}
                />
              ) : null}
            </div>
          ) : job.sourceAssetUrl ? (
            <a href={job.sourceAssetUrl} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">
              Open the uploaded PDF
            </a>
          ) : null}
          {page?.url ? (
            <a href={page.url} target="_blank" rel="noreferrer noopener" className="truncate text-xs text-muted-foreground hover:text-primary">
              {page.url}
            </a>
          ) : null}
          {ex.design ? (
            <div className="grid gap-2 rounded-lg border p-3">
              <div className="text-sm font-medium">Design we detected</div>
              <div className="flex gap-1">
                {(["backgroundColor", "primaryColor", "accentColor", "textColor"] as const).map((k) =>
                  ex.design?.tokens[k] ? <span key={k} title={k} className="size-7 rounded-md border" style={{ background: ex.design.tokens[k] }} /> : null,
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                Fonts: {ex.design.tokens.fontHeading ?? "—"} / {ex.design.tokens.fontBody ?? "—"} · Closest theme: {theme?.name}
                {themeLocked ? " (Pro — Basic will be used)" : ""}
              </div>
              <ul className="list-disc pl-5 text-[11px] text-muted-foreground">
                {ex.design.evidence.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </Card>
        <div className="grid content-start gap-5">
          {categories.map((cat) => (
            <div key={cat} className="grid gap-2">
              <Input
                value={cat}
                className="h-9 font-semibold"
                onChange={(e) => setEx({ ...ex, categories: ex.categories.map((c) => (c.name === cat ? { ...c, name: e.target.value } : c)), items: ex.items.map((i) => (i.category === cat ? { ...i, category: e.target.value } : i)) })}
              />
              {ex.items.map((item, i) =>
                item.category === cat ? (
                  <ItemEditor
                    key={i}
                    item={item}
                    currency={currency}
                    selected={selected === i}
                    onSelect={() => setSelected(i)}
                    onChange={(it) => update(i, it)}
                    onRemove={() => setEx({ ...ex, items: ex.items.filter((_, j) => j !== i) })}
                  />
                ) : null,
              )}
              <Button
                variant="ghost"
                size="sm"
                className="justify-self-start"
                onClick={() => setEx({ ...ex, items: [...ex.items, { name: "New dish", category: cat, needsReview: true, reviewReasons: ["Added manually"] }] })}
              >
                <Plus /> Add dish
              </Button>
            </div>
          ))}
          {ex.items.length === 0 ? <EmptyState title="No dishes found" description="Try a different page of the website or a PDF version of your menu." /> : null}
        </div>
      </div>
      <Card className="sticky bottom-4 gap-4 shadow-lg">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Apply as">
            <NativeSelect value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
              <option value="new_menu">New menu</option>
              {job.menuId ? <option value="append">Add to existing menu</option> : null}
              {job.menuId ? <option value="replace">Replace existing menu content</option> : null}
            </NativeSelect>
          </Field>
          {mode === "new_menu" ? (
            <Field label="Menu name">
              <Input value={menuName} onChange={(e) => setMenuName(e.target.value)} />
            </Field>
          ) : (
            <div />
          )}
          <label className="flex items-center justify-between gap-3 self-end text-sm">
            <span>Use the detected colors & fonts</span>
            <Switch checked={applyDesign} onCheckedChange={setApplyDesign} />
          </label>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">Everything goes into a draft. Flagged prices and allergen notes must be confirmed before you can publish.</p>
          <Button onClick={apply} loading={applying} disabled={!ex.items.length}>
            Create draft menu
          </Button>
        </div>
      </Card>
    </div>
  );
}

export function ImportJobPage() {
  const { jobId } = useParams({ from: "/app/imports/$jobId" });
  const job = useImport(jobId);
  const qc = useQueryClient();
  const j = job.data;
  useEffect(() => {
    if (j && !["queued", "running"].includes(j.status)) void qc.invalidateQueries({ queryKey: ["org"] });
  }, [j?.status, qc]);
  const stageLabel: Record<string, string> = {
    queued: "Waiting for a worker…",
    fetching: "Opening the website…",
    discovering: "Looking for menu pages…",
    crawling: "Reading menu pages…",
    rendering: "Capturing colors and fonts…",
    parsing: "Reading the PDF…",
    extracting: "Extracting dishes and prices…",
    normalizing: "Tidying up categories and items…",
    vision: "Reading scanned pages…",
    design: "Matching your look to a theme…",
    review: "Ready for review",
    retrying: "Retrying…",
  };
  return (
    <PageContainer wide>
      <PageHeader
        title={j?.sourceType === "pdf" ? "PDF import" : "Website import"}
        description={j?.sourceUrl ?? undefined}
        actions={
          j && ["queued", "running"].includes(j.status) ? (
            <Button
              variant="outline"
              onClick={async () => {
                await api.post(`/imports/${jobId}/cancel`);
                await job.refetch();
              }}
            >
              Cancel
            </Button>
          ) : null
        }
      />
      {!j ? null : j.status === "queued" || j.status === "running" ? (
        <Card className="mx-auto max-w-xl gap-4 p-8 text-center">
          <div className="font-medium">{stageLabel[j.stage ?? "queued"] ?? "Working…"}</div>
          <Progress value={j.progress} />
          <p className="text-sm text-muted-foreground">This usually takes under a minute. You can leave this page — the import keeps running.</p>
        </Card>
      ) : j.status === "failed" || j.status === "cancelled" ? (
        <Card className="mx-auto max-w-xl gap-3 p-8 text-center">
          <div className="font-medium">{j.status === "failed" ? "The import didn’t work" : "Import cancelled"}</div>
          <p className="text-sm text-muted-foreground">{j.error}</p>
          <p className="text-xs text-muted-foreground">Credits for this import were refunded.</p>
          <Button asChild className="justify-self-center">
            <Link to="/imports/new" search={{ type: j.sourceType }}>
              Try again
            </Link>
          </Button>
        </Card>
      ) : j.status === "completed" ? (
        <Card className="mx-auto max-w-xl gap-3 p-8 text-center">
          <div className="font-medium">This import was applied</div>
          {j.resultMenuId ? (
            <Button asChild className="justify-self-center">
              <Link to="/menus/$menuId" params={{ menuId: j.resultMenuId }}>
                Open the menu
              </Link>
            </Button>
          ) : null}
        </Card>
      ) : j.extraction ? (
        <Review job={j} />
      ) : null}
    </PageContainer>
  );
}
